import React, { useContext, useState, useEffect } from "react";
import { Modal, Box, TextField, Button, Typography, IconButton, } from "@mui/material";
import { ThemeContext } from "../../../context/ThemeContext";
import { shallowEqual, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import SLInput from "../trading/components/SLInput";
import TPInput from "../trading/components/TPInput";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";

const AddEditModal = ({ isOpenEdit, setIsOpenEdit, isEdit, formData, handleChange, handleEdit, handlePlaceOrder, sltp, sltpArray }) => {
    const { darkMode } = useContext(ThemeContext);
    const { allSymbolParams } = useSelector((state) => state.symbol);
    const [slValidation, setSlValidation] = useState(0)
    const [tpValidation, setTpValidation] = useState(0)
    const [isDisabled, setIsDisabled] = useState(false)
    const symbol = useSelector(state => state.updateSymbol.symbols?.[sltp.symbolId])
    const symbolParameterData = allSymbolParams?.[sltp?.symbolId]
    const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);
    const { t } = useTranslation()
    const positions = useSelector((state) => state.orderPosition.positions, shallowEqual);
    const positionId = sltp.positionId;
    const matchedPosition = positions.find((pos) => pos.id === positionId);
    const totalAmount = sltpArray.reduce((sum, item) => sum + item.amount, 0);

    const maxAmount = Number(((matchedPosition?.amount ?? 0) - (totalAmount ?? 0)).toFixed(decimalPlaces));

    useEffect(() => {
        // const symbol = symbols?.[sltp?.symbolId]
        const stopOffset = (symbolParameterData?.stopOffset ?? 10) * (1 / Math.pow(10, symbol.digits));
        const limitOffset = (symbolParameterData.limitOffset ?? 10) * (1 / Math.pow(10, symbol.digits));

        // Stop Loss Calculations
        const stopLoss = sltp?.side === 1
            ? (symbol?.ask + stopOffset).toFixed(symbol.digits) // Buy Condition
            : (symbol?.bid - limitOffset).toFixed(symbol.digits); // Sell Condition
        setSlValidation(stopLoss)

        // Take Profit Calculations
        const takeProfit = sltp?.side === 1
            ? (symbol?.ask - limitOffset).toFixed(symbol.digits)  // Buy Condition
            : (symbol?.bid + stopOffset).toFixed(symbol.digits); // Sell Condition
        setTpValidation(takeProfit)

        const amountValid = formData.amount && parseFloat(formData.amount) > 0;

        if (sltp.side === 1) {
            const slValid = parseFloat(formData.st) > parseFloat(stopLoss);
            const tpValid = parseFloat(formData.tp) < parseFloat(takeProfit);
            setIsDisabled(!((slValid || tpValid) && amountValid));
        } else {
            const slValid = parseFloat(formData.st) < parseFloat(stopLoss);
            const tpValid = parseFloat(formData.tp) > parseFloat(takeProfit);
            setIsDisabled(!((slValid || tpValid) && amountValid));
        }

    }, [symbol, allSymbolParams, sltp, formData])

    const [volume, setVolume] = useState(formData.amount)
    const handleBlur = (e) => {
        const value = e.target.value
        if (volume > symbolParameterData?.minimumAmountPerDeal && volume < symbolParameterData?.maximumAmountPerDeal) {
            const newVal = Math.round(value / symbolParameterData?.minimumAmountPerDeal) * symbolParameterData?.minimumAmountPerDeal
            setVolume(newVal.toFixed(decimalPlaces))
        } else {
            if (volume < symbolParameterData?.minimumAmountPerDeal) {
                setVolume(symbolParameterData?.minimumAmountPerDeal)
            } else if (volume > symbolParameterData?.maximumAmountPerDeal) {
                setVolume(symbolParameterData?.maximumAmountPerDeal)
            }
        }
    }
    useEffect(() => {
        if (isOpenEdit && !formData.amount && maxAmount > 0) {
            handleChange({
                target: {
                    name: 'amount',
                    value: maxAmount.toFixed(decimalPlaces),
                },
            });
        }
    }, [isOpenEdit, maxAmount, decimalPlaces]);

    return (
        <Modal open={isOpenEdit} maxWidth="md" width="md" onClose={e => setIsOpenEdit(false)}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: darkMode ? "var(--background-color)" : 'var(--white)', }}>
                <Box className="bg-primary" sx={{ padding: '5px', px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography id="edit-position-modal" variant="body1" sx={{ color: "var(--white)" }} >{!isEdit ? "Add" : "Edit"} SLTP</Typography>
                    <IconButton onClick={e => setIsOpenEdit(false)} sx={{ color: "var(--white)" }}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Box>
                <Box sx={{ padding: 2 }} container>
                    <div className="grid grid-cols-2">
                        <div className="mx-2 mb-1">
                            <div className="mb-0.5 font-medium text-sm" style={{ color: darkMode ? 'var(--lightText)' : "var(--black)" }}>{t('stopLoss')} {sltp.side === 1 ? '>' : '<'} {slValidation} </div>
                            <SLInput stopLoss={formData.st} setStopLoss={(val) => handleChange({ target: { name: 'st', value: val } })} slValidation={slValidation} symbolData={symbol} side={sltp.side} />
                        </div>
                        <div className="mx-2 mb-1">
                            <div className="mb-0.5 font-medium text-sm" style={{ color: darkMode ? 'var(--lightText)' : "var(--black)" }}>{t('takeProfit')} {sltp.side === 1 ? '<' : '>'} {tpValidation} </div>
                            <TPInput takeProfit={formData.tp} setTakeProfit={(val) => handleChange({ target: { name: 'tp', value: val } })} tpValidation={tpValidation} symbolData={symbol} />
                        </div>

                        <div className="mx-2 mt-2">
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography sx={{ fontSize: '0.9rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('amount')}</Typography>
                                <Typography align="right" sx={{ fontSize: '0.6rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{`${t('min')}: ${symbolParameterData?.minimumAmountPerDeal}, ${t('max')}: ${maxAmount}`}</Typography>

                            </Box>
                            <Box sx={{ px: 1, display: "flex", justifyContent: 'center', alignItems: "center", width: '100%', height: '36px', border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, backgroundColor: 'transparent' }}>
                                <RemoveIcon sx={{ cursor: 'pointer', fontSize: '1rem', color: darkMode ? 'white' : "gray" }}
                                    onClick={() => {
                                        const step = symbolParameterData?.minimumAmountPerDeal || 0.01;
                                        const min = symbolParameterData?.minimumAmountPerDeal || 0.01;
                                        const current = parseFloat(formData.amount) || min;
                                        const newVal = Math.max(current - step, min).toFixed(decimalPlaces);
                                        handleChange({ target: { name: 'amount', value: newVal } });
                                    }}
                                />
                                <TextField name="amount" value={formData.amount} variant="filled" type="number" onChange={handleChange} InputProps={{ disableUnderline: true }}
                                    onBlur={handleBlur}

                                    sx={{ '& .MuiFilledInput-root': { width: 180, backgroundColor: 'transparent', fontSize: '0.9rem', textAlign: 'center', }, '& .MuiFilledInput-input': { padding: 0, px: 1, textAlign: "center", backgroundColor: 'transparent', } }}
                                />
                                <AddIcon
                                    sx={{ cursor: 'pointer', fontSize: '1rem', color: darkMode ? 'white' : "gray" }}
                                    onClick={() => {
                                        const step = symbolParameterData?.minimumAmountPerDeal || 0.01;
                                        const max = symbolParameterData?.maximumAmountPerDeal || 100;
                                        const current = parseFloat(formData.amount) || step;
                                        const newVal = Math.min(current + step, max).toFixed(decimalPlaces);
                                        handleChange({ target: { name: 'amount', value: newVal } });
                                    }}
                                />
                            </Box>
                        </div>

                        <div className="mx-2  mt-auto">
                            <Button onClick={isEdit ? handleEdit : handlePlaceOrder} variant="contained" size="small" sx={{ height: '36px', width: '100%' }} disabled={isDisabled}>
                                {isEdit ? `${t('edit')} SLTP` : `${t('add')} SLTP`}
                            </Button>
                        </div>
                    </div>
                </Box>
            </Box>
        </Modal>
    )
}

export default AddEditModal